import { useEffect } from "react"

import type { WidgetFieldsFragment } from "~graphql/generated/graphql"
import COPY from "~config/copy-constants"
import { captureSentryException } from "~root/sentry"

const DEFAULT_BUTTON_COLOR = "#f65e38"
const BLACK = "#000"
const WHITE = "#fff"
const BOOLEAN_STRINGS = {
    TRUE: "true",
    FALSE: "false",
}

// Iframe needs to be 2px wider when widget is rendered
// inline to prevent cutting off the border.
export const MAX_INLINE_IFRAME_WIDTH = "1026px"
export const MAX_WIDGET_WIDTH = "1280px"
export const WIDGET_HEIGHT = "728px"
export const WIDGET_INLINE_HEIGHT = "700px"

export interface WidgetAttributes {
    widgetId: string | null
    outfitterId: string | null
    isAlreadyLoaded: boolean
    shouldUseParent: boolean
    shouldOverrideModal: boolean
    shouldNotLoadHidden: boolean
    mobileAlign: string | null
    desktopAlign: string | null
    isMobileInline: boolean
    isDesktopInline: boolean
    triggerType: string | null
    buttonColor: string | null
    textColor: string | null
    text: string | null
    isExternal: boolean
}

export interface TriggerSettings {
    triggerType: string
    buttonColor: string
    textColor: string
    text: string
    isExternal: boolean
}

export const WIDGET_TRIGGER_TYPES = {
    BUTTON: "button",
    INLINE: "inline",
    MODAL: "modal",
    TAB: "tab",
}

export const WIDGET_ALIGN = {
    LEFT: "left",
    RIGHT: "right",
}

export const ATTRIBUTES = {
    ID: "data-id",
    WIDGET_ID: "data-widget-id",
    OUTFITTER: "data-outfitter",
    MB_LOADED: "data-mb-loaded",
    BUTTON_OVERRIDE: "data-button-override",
    DONT_LOAD_HIDDEN: "data-dont-load-hidden",
    ALIGN_MOBILE: "data-mb-align-mobile",
    ALIGN_DESKTOP: "data-mb-align-desktop",
    INLINE_MOBILE: "data-mb-inline-mobile",
    INLINE_DESKTOP: "data-mb-inline-desktop",
    LOAD_ON_CHANGE: "data-load-on-change",
    MODAL_OVERRIDE: "data-modal-override",
    TRIGGER: "data-trigger",
    BUTTON_COLOR: "data-button-color",
    TEXT_COLOR: "data-text-color",
    TEXT: "data-text",
    EXTERNAL: "data-external",
}

export const CLASS_NAMES = {
    DESKTOP_INLINE: "mb-desktop-inline",
    MOBILE_INLINE: "mb-mobile-inline",
    FULL_WIDTH: "mb-full-width",
    WIDGET: "mb_widget",
}

export function getWidgetAttributes(element: Element): WidgetAttributes {
    const id = getStringAttribute(element, ATTRIBUTES.ID)
    const widgetId = getStringAttribute(element, ATTRIBUTES.WIDGET_ID) ?? id
    const outfitterId = getStringAttribute(element, ATTRIBUTES.OUTFITTER)
    const isAlreadyLoaded = getBooleanAttribute(element, ATTRIBUTES.MB_LOADED)
    const shouldUseParent = getBooleanAttribute(
        element,
        ATTRIBUTES.BUTTON_OVERRIDE
    )
    const shouldOverrideModal = getBooleanAttribute(
        element,
        ATTRIBUTES.MODAL_OVERRIDE
    )
    const shouldNotLoadHidden = getBooleanAttribute(
        element,
        ATTRIBUTES.DONT_LOAD_HIDDEN
    )
    const mobileAlign = getStringAttribute(element, ATTRIBUTES.ALIGN_MOBILE)
    const desktopAlign = getStringAttribute(element, ATTRIBUTES.ALIGN_DESKTOP)
    const isMobileInline = getBooleanAttribute(
        element,
        ATTRIBUTES.INLINE_MOBILE
    )
    const isDesktopInline = getBooleanAttribute(
        element,
        ATTRIBUTES.INLINE_DESKTOP
    )
    const triggerType = getStringAttribute(element, ATTRIBUTES.TRIGGER)
    const buttonColor = getStringAttribute(element, ATTRIBUTES.BUTTON_COLOR)
    const textColor = getStringAttribute(element, ATTRIBUTES.TEXT_COLOR)
    const text = getStringAttribute(element, ATTRIBUTES.TEXT)
    const isExternal = getBooleanAttribute(element, ATTRIBUTES.EXTERNAL)

    return {
        widgetId,
        outfitterId,
        isAlreadyLoaded,
        shouldUseParent,
        shouldOverrideModal,
        shouldNotLoadHidden,
        mobileAlign,
        desktopAlign,
        isMobileInline,
        isDesktopInline,
        triggerType,
        buttonColor,
        textColor,
        text,
        isExternal,
    }
}

export function getTriggerSettings({
    widget,
    widgetAttributes,
}: {
    widget: WidgetFieldsFragment | null
    widgetAttributes: WidgetAttributes
}) {
    const triggerType =
        widgetAttributes.triggerType ??
        widget?.trigger_type.toLowerCase() ??
        WIDGET_TRIGGER_TYPES.BUTTON
    const buttonColor =
        widgetAttributes.buttonColor ??
        widget?.button_color ??
        DEFAULT_BUTTON_COLOR
    const defaultTextColor = isModalTriggerType(triggerType) ? BLACK : WHITE
    const textColor =
        widgetAttributes.textColor ?? widget?.text_color ?? defaultTextColor
    const text = widgetAttributes.text ?? widget?.button_text ?? COPY.BOOK_NOW
    const isExternal =
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO fix eslint
        widgetAttributes.isExternal ?? widget?.is_external ?? false

    return { triggerType, buttonColor, textColor, text, isExternal }
}

export function setIsAlreadyLoadedAttribute(element: Element | null) {
    element?.setAttribute(ATTRIBUTES.MB_LOADED, BOOLEAN_STRINGS.TRUE)
}

export function getBooleanAttribute(
    element: Element | null,
    attributeName: string
) {
    const attribute = element?.getAttribute(attributeName)

    return attribute === BOOLEAN_STRINGS.TRUE
}

function getStringAttribute(element: Element | null, attributeName: string) {
    const attribute = element?.getAttribute(attributeName)

    if (!attribute) return null

    return attribute
}

export function getMbWidgetRoute(outfitterId: string | null) {
    return `${process.env.GATSBY_MB_ROOT_URL}/widget/${outfitterId}`
}

export function isInlineTriggerType(triggerType: string | null) {
    return triggerType === WIDGET_TRIGGER_TYPES.INLINE
}

export function isModalTriggerType(triggerType: string | null) {
    return triggerType === WIDGET_TRIGGER_TYPES.MODAL
}

function trackWidgetLoadEvent({
    widgetAttributes,
}: {
    widgetAttributes: WidgetAttributes
}) {
    const { widgetId } = widgetAttributes
    // This is one of the way to trace the origin URL rendering the widget iframe
    const locationOrigins = window.location.ancestorOrigins

    if (!widgetId) {
        captureSentryException(new Error(COPY.WIDGET_WITHOUT_ID_ERROR), {
            extra: {
                locationOrigins,
                ...widgetAttributes,
            },
        })
    }
}

export function useTrackWidgetLoadEvent({
    widgetAttributes,
}: {
    widgetAttributes: WidgetAttributes
}) {
    useEffect(() => {
        trackWidgetLoadEvent({ widgetAttributes })
    }, [])
}
